import type { TourStep } from '@/components/ui/Tour';
import type { StartTourWithCompletionKeyOptions } from '@/components/tour/PageTourHost';
import type { PageTourKey } from './registry';
import {
  clubInventoryHiresTourSteps,
  clubInventoryItemsTourSteps,
  type ClubInventoryTourOptions,
  getClubInventoryTourSteps,
} from './club-inventory-tour-steps';
import { schedulePageTourStart, scheduleTourStepsStart } from './scheduleAutoPageTour';

export type { ClubInventoryTourOptions };

export type ClubInventoryTourTab = 'items' | 'hires';

export const CLUB_INVENTORY_TOUR_KEY: PageTourKey = 'club-inventory';

export const CLUB_INVENTORY_TAB_TOUR_KEYS: Record<ClubInventoryTourTab, PageTourKey> = {
  items: 'club-inventory-items',
  hires: 'club-inventory-hires',
};

export function resolveClubInventoryTabTourKey(tab: ClubInventoryTourTab): PageTourKey {
  return CLUB_INVENTORY_TAB_TOUR_KEYS[tab];
}

export interface ClubInventoryTabHelpContent {
  title: string;
  description: string;
  tourLabel?: string;
  tourDescription?: string;
}

export const CLUB_INVENTORY_PAGE_HELP = {
  title: 'Inventory',
  description: 'Track stock levels and hireable equipment for court hire add-ons and walk-up loans.',
  tourLabel: 'Tour this page',
  tourDescription: 'Walk through items, hires, and creating an inventory item.',
};

export const CLUB_INVENTORY_TAB_HELP: Record<ClubInventoryTourTab, ClubInventoryTabHelpContent> = {
  items: {
    title: 'Inventory items',
    description: 'Track stock levels and hireable equipment for court hire add-ons and walk-up loans.',
  },
  hires: {
    title: 'Hires',
    description: 'Review active and completed equipment loans.',
  },
};

export function getClubInventoryTabHelp(tab: ClubInventoryTourTab): ClubInventoryTabHelpContent {
  return CLUB_INVENTORY_TAB_HELP[tab];
}

export function getClubInventoryTabTourSteps(tab: ClubInventoryTourTab): TourStep[] {
  return tab === 'hires' ? clubInventoryHiresTourSteps : clubInventoryItemsTourSteps;
}

export function scheduleClubInventoryTourStart(
  startTourWithCompletionKey: (
    completionKey: string,
    steps: TourStep[],
    options?: StartTourWithCompletionKeyOptions,
  ) => void,
  options: ClubInventoryTourOptions = {},
): () => void {
  const steps = getClubInventoryTourSteps(options);
  return scheduleTourStepsStart(
    () =>
      startTourWithCompletionKey(CLUB_INVENTORY_TOUR_KEY, steps, {
        onFinish: options.onFinish,
      }),
    steps,
  );
}

export function scheduleClubInventoryTabTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  tab: ClubInventoryTourTab,
  onEnterTab?: () => void,
): () => void {
  const tourKey = resolveClubInventoryTabTourKey(tab);
  const steps = getClubInventoryTabTourSteps(tab).map((step, index) => {
    if (index === 0 && onEnterTab) {
      return { ...step, onStepEnter: onEnterTab };
    }
    return step;
  });
  return schedulePageTourStart(
    () => startTourWithCompletionKey(tourKey, steps),
    tourKey,
  );
}
